import {SupportTicket} from "../../../models/support";
import {useEffect, useState} from "react";
import {adminFetchSupportTicket, fetchSupportTicket} from "../../../components/business_logic/support_ticket_requests";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

interface PreviewSupportTicketProps {
    ticket: SupportTicket;
    isAdmin: boolean;
}

export default function ViewSupportTicket({ticket, isAdmin}: PreviewSupportTicketProps) {
    const [fullTicket, setFullTicket] = useState({} as SupportTicket);
    
    useEffect(() => {
        if (isAdmin) {
            adminFetchSupportTicket(ticket).then(t => {
                if (t && Object.keys(t).length > 0) {
                    setFullTicket(t);
                }
            })
        } else {
            fetchSupportTicket(ticket).then(t => {
                if (t && Object.keys(t).length > 0) {
                    setFullTicket(t);
                }
            })
        }
    }, [ticket, isAdmin])

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatStatus = (status?: string) => {
        if (!status) return 'N/A';
        return status.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const formatBuildValue = (value?: string) => {
        if (!value) return 'N/A';
        return value.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const displayTicket = fullTicket && Object.keys(fullTicket).length > 0 ? fullTicket : ticket;

    return (
        <Container 
            id="support-ticket-preview"
            sx={{
                pt: { xs: 4, sm: 6 },
                pb: { xs: 4, sm: 6 },
                px: { xs: 3, sm: 4 },
                maxWidth: '800px',
                margin: '0 auto'
            }}
        >
            {/* Header Section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography 
                    component="h1" 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    {displayTicket.title || 'Untitled Support Ticket'}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Metadata */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 3,
                        flexWrap: 'wrap',
                        mb: 2
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        <strong>Status:</strong> {formatStatus(displayTicket.ticket_status)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Created:</strong> {formatDate(displayTicket.created_at)}
                    </Typography>
                    {displayTicket.updated_at && displayTicket.updated_at !== displayTicket.created_at && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Updated:</strong> {formatDate(displayTicket.updated_at)}
                        </Typography>
                    )}
                    {displayTicket.user && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>User:</strong> {displayTicket.user.username || displayTicket.user.email || 'N/A'}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Build Information Section */}
            {displayTicket.build && (
                <Box 
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        p: { xs: 3, sm: 4 },
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 3
                    }}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.primary',
                            fontWeight: 600,
                            mb: 2
                        }}
                    >
                        Build Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {displayTicket.build.version_number && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Version:</strong> {displayTicket.build.version_number}
                            </Typography>
                        )}
                        {displayTicket.build.platform && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Platform:</strong> {formatBuildValue(displayTicket.build.platform)}
                            </Typography>
                        )}
                        {displayTicket.build.track && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Track:</strong> {formatBuildValue(displayTicket.build.track)}
                            </Typography>
                        )}
                        {displayTicket.build.deployment_type && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Deployment Type:</strong> {formatBuildValue(displayTicket.build.deployment_type)}
                            </Typography>
                        )}
                        {displayTicket.build.released_at && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Released:</strong> {formatDate(displayTicket.build.released_at)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {/* Description Section */}
            <Box 
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    p: { xs: 3, sm: 4 },
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    Description
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {displayTicket.description ? (
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: 'text.primary',
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {displayTicket.description}
                    </Typography>
                ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Loading description...
                    </Typography>
                )}
            </Box>
        </Container>
    );
}